import React from "react";
import { Col, Row, Container, Spinner } from "react-bootstrap";
import Dropdown from "react-bootstrap/Dropdown";
import {
  AmbientLight,
  AxesHelper,
  DirectionalLight,
  GridHelper,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  Raycaster,
  Vector2,
  MeshLambertMaterial,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

import { IFCLoader } from "web-ifc-three/IFCLoader";

let IfcContainer = (props) => {
  let [showBIMProperties, setShowBIMProperties] = React.useState(false);
  let [expressId, setExpressId] = React.useState("");
  let [value, setValue] = React.useState("");
  let [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const size = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    const scene = new Scene();
    const lightColor = 0xffffff;
    const ambientLight = new AmbientLight(lightColor, 0.5);
    scene.add(ambientLight);
    const directionalLight = new DirectionalLight(lightColor, 1);
    directionalLight.position.set(0, 10, 0);
    directionalLight.target.position.set(-5, 0, 0);
    scene.add(directionalLight);
    scene.add(directionalLight.target);

    const grid = new GridHelper(50, 30);
    scene.add(grid);

    const axes = new AxesHelper();
    axes.material.depthTest = false;
    axes.renderOrder = 1;
    scene.add(axes);

    const threeCanvas = document.getElementById("three-canvas");
    const renderer = new WebGLRenderer({
      canvas: threeCanvas,
      alpha: true,
    });

    renderer.setSize(size.width, size.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const aspect = size.width / size.height;
    const camera = new PerspectiveCamera(75, aspect);
    camera.position.z = 15;
    camera.position.y = 13;
    camera.position.x = 8;

    const controls = new OrbitControls(camera, threeCanvas);
    controls.enableDamping = true;
    controls.target.set(-2, 0, 0);

    const animate = () => {
      controls.update();
      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    };

    animate();

    const ifcLoader = new IFCLoader();
    ifcLoader.ifcManager.setWasmPath("/");
    ifcLoader.ifcManager.setOnProgress((ev) => {
      if ((ev.loaded / ev.total) * 100 > 90) setLoading(false);
    });

    const ifcModels = [];
    ifcLoader.load(props.ifcFile, (ifcModel) => {
      ifcModels.push(ifcModel.mesh);
      scene.add(ifcModel.mesh);
    });

    const raycaster = new Raycaster();
    raycaster.firstHitOnly = true;
    const mouse = new Vector2();

    function cast(event) {
      const bounds = threeCanvas.getBoundingClientRect();
      const x1 = event.clientX - bounds.left;
      const x2 = bounds.right - bounds.left;
      mouse.x = (x1 / x2) * 2 - 1;
      const y1 = event.clientY - bounds.top;
      const y2 = bounds.bottom - bounds.top;
      mouse.y = -(y1 / y2) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      return raycaster.intersectObjects(ifcModels);
    }

    const preselectMat2 = new MeshLambertMaterial({
      transparent: true,
      opacity: 0.6,
      color: 0x172beb,
      depthTest: false,
    });

    let preselectModel2 = { id: -1 };

    function pick(event) {
      const found = cast(event)[0];
      highlight(event, preselectMat2, preselectModel2);
      if (found) {
        const index = found.faceIndex;
        const geometry = found.object.geometry;
        const ifc = ifcLoader.ifcManager;
        const id = ifc.getExpressId(geometry, index);
        const modelID = found.object.modelID;
        ifc.getItemProperties(modelID, id).then((x) => {
          setShowBIMProperties(true);
          setValue(x["Name"]["value"]);
          setExpressId(x["expressID"]);
        });
      } else {
        setShowBIMProperties(false);
        setValue("");
        setExpressId("");
      }
    }

    threeCanvas.onclick = pick;

    const preselectMat = new MeshLambertMaterial({
      transparent: true,
      opacity: 0.6,
      color: 0x29f2f2,
      depthTest: false,
    });
    let preselectModel = { id: -1 };

    function highlight(event, material, model) {
      const found = cast(event)[0];
      const ifc = ifcLoader.ifcManager;
      if (found) {
        model.id = found.object.modelID;
        const index = found.faceIndex;
        const geometry = found.object.geometry;
        const id = ifc.getExpressId(geometry, index);

        ifcLoader.ifcManager.createSubset({
          modelID: model.id,
          ids: [id],
          material: material,
          scene: scene,
          removePrevious: true,
        });
      } else {
        ifc.removeSubset(model.id, scene, material);
      }
    }

    window.onmousemove = (e) => highlight(e, preselectMat, preselectModel);

    window.addEventListener("resize", () => {
      size.width = window.innerWidth;
      size.height = window.innerHeight;
      camera.aspect = size.width / size.height;
      camera.updateProjectionMatrix();
      renderer.setSize(size.width, size.height);
    });
  }, []);

  return (
    <React.Fragment>
      <Container fluid>
        <Row>
          <Col xs={4} style={{ backgroundColor: "#404040" }}></Col>
          <Col
            xs={4}
            style={{
              backgroundColor: "#4d4d4d",
              textAlign: "center",
              lineHeight: "200%",
              color: "#cccccc",
            }}
          >
            {loading ? (
              <Spinner animation="border" variant="primary" />
            ) : (
              props.title
            )}
          </Col>
          <Col xs={3} style={{ backgroundColor: "#404040" }}></Col>
          <Col xs={1} style={{ backgroundColor: "#404040" }}>
            <Dropdown style={{ visibility: "hidden" }}>
              <Dropdown.Toggle
                style={{
                  color: "DarkGreen",
                  backgroundColor: "gray",
                  borderRadius: 0,
                  border: "2px solid black",
                }}
              >
                Pilot Cases
              </Dropdown.Toggle>
            </Dropdown>
          </Col>
        </Row>
      </Container>
      {showBIMProperties ? (
        <div
          style={{
            width: "100%",
            color: "DarkGray",
            backgroundColor: "#172beb",
            opacity: 0.5,
          }}
        >
          {" "}
          &nbsp; ExpressId: <u>{expressId}</u> &nbsp; | &nbsp; Value:{" "}
          <u> {value}</u>{" "}
        </div>
      ) : null}
      <canvas id="three-canvas"></canvas>;
    </React.Fragment>
  );
};

export default IfcContainer;
